import {
  NavigateOptions,
  To,
  useNavigate as useRouterNavigate,
  useSearchParams,
} from "react-router-dom";
import { CLIENT_ID_PARAM } from "../lib/constants";
import { appendClientIdToPath } from "../lib/utility";

/**
 * Custom navigation hook that preserves the `client` query parameter when navigating
 * This is critical for maintaining client context across all routes and page refreshes
 * @returns A navigate function that preserves the `client` query parameter
 */
export const useNavigate = () => {
  const navigate = useRouterNavigate();
  const [searchParams] = useSearchParams();

  return (to: To, options?: NavigateOptions) => {
    // If to is a string (path), handle it
    if (typeof to === "string") {
      const clientId = searchParams.get(CLIENT_ID_PARAM);
      const newTo = appendClientIdToPath(to, clientId || "");
      navigate(newTo, options);
      return;
    }
    // If to is an object with pathname and search
    else if (to && typeof to === "object" && to.pathname) {
      const clientId = searchParams.get(CLIENT_ID_PARAM);

      if (clientId) {
        // Create new URLSearchParams from existing search or empty string
        const newSearchParams = new URLSearchParams(to.search || "");
        // Add client ID if it doesn't exist
        if (!newSearchParams.has(CLIENT_ID_PARAM)) {
          newSearchParams.set("client", clientId);
        }

        // Update to object with new search params
        to = {
          ...to,
          search: newSearchParams.toString()
            ? `?${newSearchParams.toString()}`
            : "",
        };
      }
    }

    // Call the original navigate function
    navigate(to, options);
  };
};
